import { FC, memo, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Loader } from '@aws-amplify/ui-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import { businessPlanQueryKeys } from 'services/businessPlan/businessPlan-qk';
import { BusinessPlanApi } from 'services/businessPlan/businessPlan.api';
import { useGetAllAccessUsers } from 'services/businessPlan/hooks';
import { useDeleteUserAccess } from 'services/businessPlan/hooks/useDeleteUserAccess';
import { PLAN_ACCESS_TYPE } from 'services/businessPlan/types';

import { ReactComponent as ErrorOutlinedInfoIcon } from 'assets/icons/ErrorOutlinedInfoIcon.svg';

import usePromiseStatus from 'hooks/usePromiseStatus';

import { CloseButton } from 'components/_basic/CloseButton/CloseButton';
import { Modal } from 'components/_basic/Modal';
import { DataPrivacyModal } from 'components/DataPrivacyModal';
import { ShareLinksForm } from 'components/ShareLinksForm/ShareLinksForm';
import { IAccountPersonalInfoForm } from 'components/ShareLinksForm/types';

import ToastService from 'helpers/ToastService';
import { grantAccessValidationSchema } from 'helpers/validations/grantAccessValidation';

import { AssistantsList } from './AssistantsList';
import { IInviteAssistantsModalProps as IProps } from './types';

import styles from './InviteAssistantsModal.module.scss';

const EDITOR_LIMIT_WITH_OWNER = 6;

export const InviteAssistantsModal: FC<IProps> = memo(({ onClose, id }) => {
  const { t } = useTranslation();
  const { track, isPending } = usePromiseStatus();
  const [isDataPrivacyModalOpen, setIsDataPrivacyModalOpen] = useState(false);
  const [isUserAcceptedPrivacy, setIsUserAcceptedPrivacy] = useState(false);
  const { data: editorsData, isLoading } = useGetAllAccessUsers({ id });
  const deleteUserAccess = useDeleteUserAccess({ id });
  const queryClient = useQueryClient();

  const invalidateQueries = () => queryClient.invalidateQueries({ queryKey: businessPlanQueryKeys.accessUsers(id) });

  const removeEditor = useCallback(async (email: string) => {
    try {
      await track(deleteUserAccess.mutateAsync(email));
      invalidateQueries();
    } catch (error) {
      error instanceof Error && ToastService.showError(error.message);
    }
  }, []);

  const {
    register,
    formState: { isValid, errors },
    handleSubmit,
    reset,
  } = useForm<IAccountPersonalInfoForm>({
    defaultValues: {
      email: '',
    },
    resolver: yupResolver<IAccountPersonalInfoForm>(grantAccessValidationSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  if (isEmpty(editorsData) || isLoading) {
    return (
      <Box className={styles.loaderWrapper}>
        <Loader />
      </Box>
    );
  }

  const isMaxEditors = editorsData.total === EDITOR_LIMIT_WITH_OWNER;
  const isOnlyOwner = editorsData.total === 1;

  const onSubmit = async ({ email }: IAccountPersonalInfoForm) => {
    try {
      await track(
        BusinessPlanApi.grantEditAccess({
          id,
          body: {
            email,
            type: PLAN_ACCESS_TYPE.EDIT,
          },
        }),
      );

      invalidateQueries();
      reset();
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError(error.message);
      }
    }
  };

  const onCloseDataPrivacyModal = () => {
    setIsUserAcceptedPrivacy(true);
    setIsDataPrivacyModalOpen(false);
  };

  return (
    <>
      {!isDataPrivacyModalOpen && (
        <Modal close={onClose} showCloseButton={false}>
          <Box className={styles.wrapper}>
            <Grid className={styles.header} container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h3" component="h3">
                  {t('inviteAssistant')}
                </Typography>
              </Grid>
              <Grid item>
                <CloseButton onClick={onClose} />
              </Grid>
            </Grid>
            <ShareLinksForm
              register={register}
              isLoading={isPending}
              isButtonDisabled={!isValid || isPending || isMaxEditors}
              buttonText={t('invite')}
              label={t('assistantsEmail')}
              endAdornmentText={t('canEdit')}
              error={errors.email?.message}
              isInputDisabled={isMaxEditors}
              onSubmit={
                !isOnlyOwner || isUserAcceptedPrivacy
                  ? handleSubmit(onSubmit)
                  : () => {
                      setIsDataPrivacyModalOpen(true);
                    }
              }
            />
            {isMaxEditors && (
              <Typography variant="subtitle2" className={styles.maxEditorsError}>
                <ErrorOutlinedInfoIcon className={styles.infoIcon} />
                {t('maxEditors', { editors: 5 })}
              </Typography>
            )}
            {!isOnlyOwner && <AssistantsList usersList={editorsData.content} removeEditor={removeEditor} />}
          </Box>
        </Modal>
      )}

      <DataPrivacyModal onClose={onCloseDataPrivacyModal} open={isDataPrivacyModalOpen} />
    </>
  );
});
